/**
 * As transcrições de reunião, em fila.
 *
 * <p><b>Não é o acervo com outro filtro.</b> Documentos responde "onde está a spec de
 * economia", e por isso é agrupado por assunto; aqui a pergunta é "o que foi decidido, e
 * quando" — então a tela é uma linha do tempo, da primeira reunião para a última, com a
 * mais antiga em cima. Foi o pedido: ordem de upload, do primeiro ao último.
 *
 * <p>A ordenação é pelo <b>dia da reunião</b>, não pelo do envio: alguém sobe na segunda
 * a transcrição da sexta, e a história não pode ficar fora de ordem por causa disso.
 *
 * <p><b>A data é o que identifica uma reunião</b>, não o nome — que se repete toda
 * semana. Por isso ela é o marcador da linha do tempo, e não um número sequencial: "03
 * seg" diz qual reunião é aquela; "1" só diz que ela veio antes da "2". Pelo mesmo
 * motivo a fila se divide por mês: duas reuniões por semana viram uma centena por
 * temporada, e uma lista sem marco obriga a rolar tudo para achar "aquela de junho".
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TituloDaPagina } from '../../shell/TituloDaPagina';
import { LeitorDeTranscricao } from './LeitorDeTranscricao';
import {
  agrupaPorMes,
  dataDoNome,
  diaForaDoRitual,
  partesDoDia,
  ritualDe,
  tituloSemData,
} from './filaDeReunioes';
import * as api from './transcricoesApi';
import type { Fila, Transcricao } from './transcricoesApi';
import { CHAVE_TRANSCRICOES, tamanhoLegivel } from './transcricoesApi';
import './transcricoes.css';

const ACEITA = '.txt,.pdf,.html,.htm,text/plain,application/pdf,text/html';

/** O dia de hoje em ISO, no fuso de quem está olhando — e não em UTC. */
function hojeISO(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

export function Transcricoes() {
  const fila = useQueryClient();
  const [aberta, setAberta] = useState<Transcricao | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [sobre, setSobre] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [resumo, setResumo] = useState('');
  const [reuniaoEm, setReuniaoEm] = useState(hojeISO());
  const [erro, setErro] = useState<string | null>(null);
  /** Qual reunião está com os campos abertos na fila, e o rascunho dela. */
  const [editando, setEditando] = useState<number | null>(null);
  const [rascunho, setRascunho] = useState({ titulo: '', reuniaoEm: '' });

  const { data, isPending, error } = useQuery<Fila>({
    queryKey: CHAVE_TRANSCRICOES,
    queryFn: api.listar,
  });

  const recarregar = () => void fila.invalidateQueries({ queryKey: CHAVE_TRANSCRICOES });

  const enviar = useMutation({
    mutationFn: () =>
      api.enviar({
        arquivo: arquivo!,
        titulo: titulo.trim() || undefined,
        resumo: resumo.trim() || undefined,
        reuniaoEm,
      }),
    onSuccess: () => {
      recarregar();
      fecharFormulario();
    },
    onError: (e: Error) => setErro(e.message),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => api.excluir(id),
    onSuccess: () => {
      recarregar();
      setAberta(null);
    },
    onError: (e: Error) => setErro(e.message),
  });

  /**
   * Editar título e data de uma reunião já registrada.
   *
   * <p>O PATCH existia na API desde o começo e nenhuma tela o chamava — então uma data
   * errada era permanente, e a única saída era excluir e subir de novo. Como a fila é
   * ordenada pela data, isso não é um detalhe de cadastro: é a ordem da história do
   * projeto travada no valor errado.
   */
  const salvar = useMutation({
    mutationFn: ({ id, titulo, reuniaoEm }: { id: number; titulo: string; reuniaoEm: string }) =>
      api.salvar(id, { titulo, reuniaoEm }),
    onSuccess: () => {
      recarregar();
      setEditando(null);
    },
    onError: (e: Error) => setErro(e.message),
  });

  function fecharFormulario() {
    setEnviando(false);
    setArquivo(null);
    setSobre(false);
    setTitulo('');
    setResumo('');
    setReuniaoEm(hojeISO());
    setErro(null);
  }

  /**
   * O arquivo escolhido preenche o resto do formulário.
   *
   * <p>O nome carrega as duas informações que mais custam caro erradas: o título e a
   * data. `08-03 - GG Weekly Updates` diz qual reunião é e quando ela foi — e a data
   * do nome vale mais que o padrão do campo, que é hoje. Foi assim que duas reuniões
   * de segunda e quarta entraram na fila as duas como quinta.
   */
  function escolher(f: File | null) {
    if (!f) return;
    setArquivo(f);
    // O título perde a data: ela virou campo próprio, e é o quadrado da fila que a
    // mostra. Repetida no nome, ela aparece duas vezes na mesma linha.
    if (!titulo) setTitulo(tituloSemData(f.name));
    const doNome = dataDoNome(f.name, new Date());
    if (doNome) setReuniaoEm(doNome);
  }

  function abrirEdicao(t: Transcricao) {
    setEditando(t.id);
    // A data escondida no título é a melhor sugestão que existe para corrigi-lo — foi
    // dela que a fila descobriu que o dia estava errado.
    setRascunho({
      titulo: tituloSemData(t.titulo) || t.titulo,
      reuniaoEm: dataDoNome(t.titulo, new Date()) ?? t.reuniaoEm,
    });
  }

  if (isPending) return <div className="rm-estado">carregando as transcrições…</div>;
  if (error) return <div className="rm-estado">não consegui carregar: {error.message}</div>;

  const lista = data.transcricoes;
  const meses = agrupaPorMes(lista);

  // O ritual do que está sendo enviado, para avisar antes de subir com a data errada.
  const ritualNovo = ritualDe(titulo);
  const dataSuspeita = diaForaDoRitual(reuniaoEm, ritualNovo);

  return (
    <div className="tr">
      <TituloDaPagina>
        O que foi dito nas reuniões, da primeira para a última.{' '}
        {lista.length === 0
          ? 'Nenhuma ainda.'
          : `${lista.length} ${lista.length === 1 ? 'reunião registrada' : 'reuniões registradas'}.`}
      </TituloDaPagina>

      {erro && (
        <div className="tr-erro" role="alert">
          {erro}
          <button onClick={() => setErro(null)} aria-label="Fechar">
            ×
          </button>
        </div>
      )}

      {enviando ? (
        <form
          className="tr-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (arquivo) enviar.mutate();
          }}
        >
          {/*
            A área de soltar, e não o `input type=file` cru — que traz o botão e a
            fonte do sistema, as duas coisas mais fáceis de reconhecer como "não
            desenhado". É o mesmo desenho do acervo de Documentos, que recebe arquivo
            pela mesma via.
          */}
          <label
            className={`tr-solta${sobre ? ' sobre' : ''}${arquivo ? ' cheio' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setSobre(true);
            }}
            onDragLeave={() => setSobre(false)}
            onDrop={(e) => {
              e.preventDefault();
              setSobre(false);
              escolher(e.dataTransfer.files?.[0] ?? null);
            }}
          >
            <input type="file" accept={ACEITA} onChange={(e) => escolher(e.target.files?.[0] ?? null)} />
            {arquivo ? (
              <>
                <b>{arquivo.name}</b>
                <span>{tamanhoLegivel(arquivo.size)} · clique ou solte outro para trocar</span>
              </>
            ) : (
              <>
                <b>Solte a transcrição aqui</b>
                <span>ou clique para escolher · .txt, .pdf ou .html</span>
              </>
            )}
          </label>

          <div className="tr-campos">
            <label className="tr-campo">
              <span>Título</span>
              <input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex.: Alinhamento de escopo da Season 1"
              />
              {ritualNovo && (
                <small className="tr-ritual-dica" style={{ '--c': ritualNovo.cor } as React.CSSProperties}>
                  {ritualNovo.rotulo} · acontece toda {ritualNovo.nomeDoDia}
                </small>
              )}
            </label>

            <label className="tr-campo tr-data">
              <span>Data da reunião</span>
              <input type="date" value={reuniaoEm} onChange={(e) => setReuniaoEm(e.target.value)} />
              {/*
                O aviso existe porque o erro é silencioso: sem informar a data, o envio
                assume hoje, e a fila inteira é ordenada por esse campo. Uma Weekly numa
                quinta é quase sempre a transcrição da segunda subida com o padrão.
              */}
              {dataSuspeita ? (
                <small className="tr-aviso">
                  ⚠ {partesDoDia(reuniaoEm).semana}? A {ritualNovo!.rotulo} é toda{' '}
                  {ritualNovo!.nomeDoDia} — confira se não é a data do upload.
                </small>
              ) : (
                <small>É o que ordena a fila — não a data do upload.</small>
              )}
            </label>

            <label className="tr-campo tr-largo">
              <span>Resumo (opcional)</span>
              <input
                value={resumo}
                onChange={(e) => setResumo(e.target.value)}
                placeholder="Uma linha sobre o que foi tratado"
              />
            </label>
          </div>

          <div className="tr-form-acoes">
            <button type="submit" className="tr-btn" disabled={!arquivo || enviar.isPending}>
              {enviar.isPending ? 'Enviando…' : 'Enviar'}
            </button>
            <button type="button" className="tr-btn fantasma" onClick={fecharFormulario}>
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        <button className="tr-nova" onClick={() => setEnviando(true)}>
          + anexar transcrição
        </button>
      )}

      {lista.length === 0 ? (
        <div className="tr-vazio">
          <b>Nenhuma reunião registrada</b>
          Anexe o .txt, o .pdf ou o .html de uma transcrição e ela entra na fila, na data
          em que a reunião aconteceu.
        </div>
      ) : (
        meses.map((mes) => (
          <section key={mes.chave} className="tr-mes">
            <h2 className="tr-mes-tit">
              {mes.rotulo}
              <span>{mes.reunioes.length}</span>
            </h2>

            <ol className="tr-fila">
              {mes.reunioes.map((t) => {
                const ritual = ritualDe(t.titulo);
                const dia = partesDoDia(t.reuniaoEm);
                const fora = diaForaDoRitual(t.reuniaoEm, ritual);
                const cor = ritual ? ({ '--c': ritual.cor } as React.CSSProperties) : undefined;

                if (editando === t.id) {
                  const doRascunho = partesDoDia(rascunho.reuniaoEm);
                  return (
                    <li key={t.id} className="tr-linha">
                      <form
                        className="tr-edicao"
                        style={cor}
                        onSubmit={(e) => {
                          e.preventDefault();
                          salvar.mutate({ id: t.id, ...rascunho });
                        }}
                      >
                        <label className="tr-campo">
                          <span>Data da reunião</span>
                          <input
                            type="date"
                            value={rascunho.reuniaoEm}
                            onChange={(e) => setRascunho({ ...rascunho, reuniaoEm: e.target.value })}
                          />
                          <small>{doRascunho.ehValida && `cai numa ${doRascunho.semana}`}</small>
                        </label>

                        <label className="tr-campo">
                          <span>Título</span>
                          <input
                            value={rascunho.titulo}
                            onChange={(e) => setRascunho({ ...rascunho, titulo: e.target.value })}
                            autoFocus
                          />
                        </label>

                        <div className="tr-edicao-acoes">
                          <button type="submit" className="tr-btn" disabled={salvar.isPending}>
                            {salvar.isPending ? 'Salvando…' : 'Salvar'}
                          </button>
                          <button
                            type="button"
                            className="tr-btn fantasma"
                            onClick={() => setEditando(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </form>
                    </li>
                  );
                }

                return (
                  <li key={t.id} className="tr-linha">
                    <button
                      className={`tr-item${ritual ? ' ritual' : ''}`}
                      style={cor}
                      onClick={() => setAberta(t)}
                    >
                      {/* A data é o marcador da linha do tempo: é ela que identifica a
                          reunião numa lista onde o nome se repete toda semana. */}
                      <span className="tr-dia">
                        <b>{dia.dia}</b>
                        <i>{dia.semana}</i>
                      </span>

                      <span className="tr-corpo">
                        <span className="tr-titulo">{t.titulo}</span>
                        {t.resumo && <span className="tr-resumo">{t.resumo}</span>}
                        <span className="tr-meta">
                          {ritual && <em className="tr-ritual">{ritual.rotulo}</em>}
                          {tamanhoLegivel(t.bytes)}
                          {t.enviadoPor && ` · ${t.enviadoPor}`}
                          {fora && (
                            <em className="tr-fora" title={`A ${ritual!.rotulo} é toda ${ritual!.nomeDoDia}`}>
                              ⚠ caiu numa {dia.semana}
                            </em>
                          )}
                        </span>
                      </span>

                      <span
                        className={`tr-selo${t.tipo === 'PDF' ? ' pdf' : t.tipo === 'HTML' ? ' html' : ''}`}
                      >
                        {t.tipo === 'PDF' ? 'PDF' : t.tipo === 'HTML' ? 'HTML' : 'TXT'}
                      </span>
                    </button>

                    {/* Fora do `.tr-item` porque botão dentro de botão é HTML inválido —
                        e porque corrigir a data não deve exigir abrir a transcrição. */}
                    <button
                      type="button"
                      className="tr-lapis"
                      title="Corrigir data e título"
                      aria-label={`Corrigir data e título de ${t.titulo}`}
                      onClick={() => abrirEdicao(t)}
                    >
                      ✎
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        ))
      )}

      {aberta && (
        <LeitorDeTranscricao
          transcricao={aberta}
          aoFechar={() => setAberta(null)}
          aoExcluir={() => {
            if (confirm(`Excluir a transcrição "${aberta.titulo}"?`)) excluir.mutate(aberta.id);
          }}
        />
      )}
    </div>
  );
}
